import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, DownloadIcon, LockIcon, ShieldCheckIcon, Trash2Icon } from 'lucide-react';
import { consentOptions, privacyPromises } from '../data/consents';
const ease = [0.23, 1, 0.32, 1] as const;
export function PrivacyCenter() {
  const [values, setValues] = useState<Record<string, boolean>>({
    terms: true
  });
  const [request, setRequest] = useState<string | null>(null);
  const toggle = (id: string) => setValues(v => ({
    ...v,
    [id]: !v[id]
  }));
  const sendRequest = (label: string) => {
    setRequest(label);
    window.setTimeout(() => setRequest(null), 2600);
  };
  return <section id="privacy" className="px-5 pb-20 lg:px-8 lg:pb-24" aria-labelledby="privacy-title" data-id="element-196" data-mp="13globboneuls">
      <div className="leaf-xl mx-auto grid max-w-7xl gap-10 bg-forest-deep px-6 py-12 text-white sm:px-12 lg:grid-cols-12 lg:gap-8 lg:p-16" data-id="element-197" data-mp="617slogm9c6o">
        <div className="lg:col-span-6" data-id="element-198" data-mp="az9eeh5ffplu">
          <h2 id="privacy-title" className="font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] sm:text-5xl" data-id="element-199" data-mp="ay83ccoev344">
            Your data, <span className="text-mint" data-id="element-200" data-mp="10x7faqknam47">your choice.</span>
          </h2>
          <p className="mt-5 max-w-md text-lg leading-relaxed text-white/70" data-id="element-201" data-mp="w55tacxodh0z">
            Taking part never means giving up control. Your personal data is not for sale, and brands only see group
            results.
          </p>
          <ul className="mt-8 max-w-md" data-id="element-202" data-mp="m9ckn4n2u7oh">
            {privacyPromises.map(p => <li key={p} className="flex items-start gap-3 border-t border-white/10 py-4 text-white/85" data-id="element-203" data-mp="h7wzzdota36m">
                <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-mint" data-id="element-204" data-mp="d7mue24xlesa" />
                {p}
              </li>)}
          </ul>
        </div>

        <div className="lg:col-span-6 lg:pl-8" data-id="element-205" data-mp="27kuhf7mo0m3d">
          <div className="leaf-md bg-canvas p-5 text-ink shadow-2xl sm:p-7" data-id="element-206" data-mp="27km63k66xoua">
            <div className="flex items-center gap-3" data-id="element-207" data-mp="22n2ruk9htc2c">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-forest" data-id="element-208" data-mp="2lqo2q9zbs4dk">
                <ShieldCheckIcon className="h-5 w-5 text-mint" data-id="element-209" data-mp="2lotfgp0l006b" />
              </span>
              <div data-id="element-210" data-mp="2qjh8902mbgqp">
                <p className="font-display text-xl font-extrabold tracking-tight" data-id="element-211" data-mp="2cpybljl0f266">Privacy center</p>
                <p className="text-sm text-muted" data-id="element-212" data-mp="2hp2j85sacrue">Try it. Flip a switch.</p>
              </div>
            </div>

            <ul className="mt-6 space-y-2" data-id="element-213" data-mp="35ewwgogioif6">
              {consentOptions.map(o => {
              const on = !!values[o.id];
              return <li key={o.id} className="flex items-center gap-4 rounded-2xl bg-white p-4 ring-1 ring-line" data-id="element-214" data-mp="39ffmccyuneeq">
                    <div className="flex-1" data-id="element-215" data-mp="2vitt5f9xidqn">
                      <p className="flex items-center gap-1.5 text-sm font-semibold" data-id="element-216" data-mp="30er7xh5jnc1b">
                        {o.title}
                        {o.required && <LockIcon className="h-3.5 w-3.5 text-muted" aria-label="Required" data-id="element-217" data-mp="1jzzhh9rykeun" />}
                      </p>
                      <p className="mt-0.5 text-sm text-muted" data-id="element-218" data-mp="1oy11bwx7ki76">{o.note}</p>
                    </div>
                    <button type="button" role="switch" aria-checked={on} aria-label={o.title} disabled={o.required} onClick={() => toggle(o.id)} className={`flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 disabled:cursor-not-allowed ${on ? 'justify-end bg-forest' : 'justify-start bg-line'} ${o.required ? 'opacity-60' : ''}`} data-id="element-219" data-mp="1hipd0uuubl4n">
                      <motion.span layout transition={{
                    type: 'spring',
                    stiffness: 700,
                    damping: 40
                  }} className="h-5 w-5 rounded-full bg-white shadow" data-id="element-220" data-mp="12pvneglutom9" />
                    </button>
                  </li>;
            })}
            </ul>

            <div className="mt-5 flex flex-wrap gap-2" data-id="element-221" data-mp="soba2wrvt0jk">
              <button type="button" onClick={() => sendRequest('Data export requested')} className="inline-flex h-11 items-center gap-2 rounded-full bg-white px-4 text-sm font-semibold ring-1 ring-line transition-colors duration-150 hover:bg-mint-soft" data-id="element-222" data-mp="smrtt6686phs">
                <DownloadIcon className="h-4 w-4" data-id="element-223" data-mp="1bfcbrskdza6t" /> Download my data
              </button>
              <button type="button" onClick={() => sendRequest('Deletion request sent')} className="inline-flex h-11 items-center gap-2 rounded-full bg-white px-4 text-sm font-semibold ring-1 ring-line transition-colors duration-150 hover:bg-mint-soft" data-id="element-224" data-mp="1gep60ydl7jjf">
                <Trash2Icon className="h-4 w-4" data-id="element-225" data-mp="3n4n10u13euq7" /> Delete my data
              </button>
            </div>
            <div className="mt-3 h-6" role="status" aria-live="polite" data-id="element-226" data-mp="24aaewazxiuol">
              <AnimatePresence data-id="element-227" data-mp="1zjby7ab13ukt">
                {request && <motion.p initial={{
                opacity: 0,
                y: 4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease
              }} className="flex items-center gap-1.5 text-sm font-semibold text-forest" data-id="element-228" data-mp="1zi0sh4y0wcr2">
                    <CheckIcon className="h-4 w-4" data-id="element-229" data-mp="2j7kotfwxbsi7" /> {request}
                  </motion.p>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}